/**
 * The offer to split a type that is doing the work of several markets.
 *
 * A RULE, NOT A WATCHER. Nothing in the room notices this on its own: the
 * board's only filing signal is "under no type at all", so a hundred products
 * in one bag read as fully filed and no persona is ever woken. It is computed
 * on every load instead, from two numbers the report already has.
 *
 * SIZE SAYS IT COULD BE SPLIT; SHARING SAYS IT SHOULD BE. A market with forty
 * rivals is the report working, so size alone would nag at healthy tables. The
 * second number is the share of a table's products that the best-covered
 * column reaches — on the SHEET, not the raw phrases, which are fragmented in
 * every group alike and would flag all of them. Below a third, the products
 * are not being compared on anything, which is what several markets sharing a
 * shelf looks like.
 *
 * So the button is always there once a table is big enough to cut up — you do
 * not have to wait to be told — and it speaks up when the numbers agree.
 *
 * AND IT CAN BE TAKEN BACK. A sorting is a judgement; the only way to judge
 * one is to see it on the board. Once a type has been split, this offers the
 * way back instead — put it back, look again, ask again.
 */
import type { BoardroomMatrixGroup } from '../../../types';

/** Enough products that the kinds inside it could each be a table. Below this,
 *  splitting gives you tables of two, which compare nothing. */
export const SPLIT_FLOOR = 12;
/** Below this share, the table is not comparing its products on anything. */
export const THIN = 1 / 3;

export function splitOffer(group: BoardroomMatrixGroup): 'no' | 'quiet' | 'loud' {
  if (!group.solution_type_id || (group.unsorted ?? 0) < SPLIT_FLOOR) return 'no';
  return (group.cohesion ?? 1) < THIN ? 'loud' : 'quiet';
}

export default function SplitIntoKinds({
  group,
  onSplit,
  onUndo,
  splitting = false,
}: {
  group: BoardroomMatrixGroup;
  onSplit?: (typeId: number) => void;
  onUndo?: (typeId: number) => void;
  splitting?: boolean;
}) {
  // ALREADY SORTED: the useful offer is the way back, not another pass over
  // the handful it left behind.
  if (group.split_at && onUndo && group.solution_type_id) {
    return (
      <button
        type="button"
        onClick={() => onUndo(group.solution_type_id!)}
        title={
          `Put back the kinds this was sorted into on ${new Date(group.split_at).toLocaleString()}.` +
          ' The products come back here and the kinds it named are dropped - any you have filed something into by hand are kept.'
        }
        className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
        data-testid={`undo-split-${group.key}`}
      >
        ↺ Undo the split
      </button>
    );
  }
  const offer = splitOffer(group);
  if (!onSplit || offer === 'no') return null;
  const n = group.unsorted ?? 0;
  const share = Math.round((group.cohesion ?? 0) * 100);
  return (
    <button
      type="button"
      disabled={splitting}
      onClick={() => onSplit(group.solution_type_id!)}
      title={
        splitting
          ? 'It is working out the kinds now'
          : `${n} products here are filed under this and nowhere narrower, and the most widely shared column reaches only ${share}% of them.` +
            ' Read them at once and name the kinds they fall into - this name stays as the one they are kinds of, and nothing already sorted moves.'
      }
      /* ONE STYLE. Two colours side by side with no key read as "you already
         pressed that one" — an invented distinction nobody can decode without
         hovering. Whether the table is worth splitting is a fact, so it goes
         in the meta line as a number, not into the button's paint. */
      className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
      data-testid={`split-type-${group.key}`}
      data-offer={offer}
    >
      {/* KINDS OF WHAT. "Split into kinds" left the reader to guess. The
          type's name is an inch to the left in the heading and spelled out in
          the tooltip, so the button says the part the heading cannot: THESE
          ones, and how many will move. */}
      {splitting ? 'sorting…' : `⑂ Split these ${n} into kinds`}
    </button>
  );
}
